<script setup lang="ts">
import { inject, reactive, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { STATUS_OPEN } from '@/constants';
import { usePromosStore } from '@/store/PromosStore';
import { useItemConfigurationStore } from '@/store/ItemConfigurationStore';
import { handleBeforeSendImage, handleSelectFile } from '@/utils/helper';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import FileUpload from 'primevue/fileupload';
import Fluid from 'primevue/fluid';
import InputText from 'primevue/inputtext';

const dialogRef = inject('dialogRef') as Ref;
const isSave = dialogRef?.value?.options.emits.isSave;
const data = dialogRef?.value?.options.props.data;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const isAddForm = !data;
const promosStore = usePromosStore();
const isEdit = ref(false);
const hideCancel = ref(false);

const configurationModule = useItemConfigurationStore();

const signatureForm = reactive(
  data
    ? { ...data }
    : {
        name: '',
        imgUrl: '',
        sts: STATUS_OPEN
      }
);
const onAdvancedUpload = (e) => {
  if (e.xhr.status === 200) {
    signatureForm.imgUrl = e.xhr.response;
    hideCancel.value = true;
    toast.add({ severity: 'info', summary: 'Success', detail: t('common.upload.success'), life: 3000 });
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('common.upload.error'), life: 5000 });
  }
};

const save = async () => {
  await configurationModule
    .saveSignature(signatureForm)
    .then(() => {
      isSave();
      toast.add({ severity: 'success', summary: '', detail: t('voucher.notification.editSuccess'), life: 3000 });
    })
    .catch(() => {
      isSave();
      toast.add({ severity: 'error', summary: '', detail: t('voucher.notification.editFailure'), life: 5000 });
    });
  isSave();
};

const edit = () => {
  isEdit.value = true;
};

const cancel = () => {
  isEdit.value = false;
};
</script>
<template>
  <div class="main">
    <div class="mt-5">
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="name">{{ $t('voucher.signatureManagement.name') }}</label>
            <InputText class="mb-1" maxlength="50" v-model="signatureForm.name" />
          </div>
        </div>
      </Fluid>
    </div>
    <div class="mt-5" v-if="!isAddForm && signatureForm.imgUrl && !isEdit">
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="name">{{ $t('voucher.signatureManagement.image') }}</label>
            <div class="flex">
              <img :src="signatureForm.imgUrl" alt="signature" class="width-300px" />
              <Button icon="pi pi-pencil" class="p-button-text" @click="edit()" />
            </div>
          </div>
        </div>
      </Fluid>
    </div>
    <div class="mt-5" v-if="isAddForm || isEdit">
      <FileUpload
        name="file"
        :url="promosStore.getUrlUploadImg"
        :multiple="true"
        accept="image/*"
        :maxFileSize="1000000"
        @select="handleSelectFile"
        @before-send="handleBeforeSendImage"
        @upload="onAdvancedUpload($event)"
        @removeUploadedFile="
          () => {
            signatureForm.imgUrl = '';
          }
        "
      >
        <template #empty>
          <p>{{ $t('promos.labels.drag_file_desc') }}</p>
        </template>
      </FileUpload>
      <div class="text-center" v-if="!hideCancel && !isAddForm">
        <Button icon="pi pi-times" class="p-button-text" @click="cancel()" />
      </div>
    </div>
    <div class="mt-5 text-center">
      <Button id="save-btn" :disabled="!signatureForm.name || !signatureForm.imgUrl" :label="$t('common.buttons.save')" @click="save()" />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.width-300px {
  width: 300px;
}
</style>
